<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition-opacity duration-200"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-opacity duration-200"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="visible"
        class="fixed inset-0 z-[100] flex cursor-pointer items-center justify-center bg-black/60 backdrop-blur-sm"
        @click="emit('close')"
      >
        <div
          class="mx-4 w-full max-w-2xl cursor-default rounded-xl border border-gray-200 bg-white p-8 shadow-2xl dark:border-gray-700 dark:bg-slate-900"
          @click.stop
        >
          <!-- Header with toggle -->
          <div class="mb-6 flex items-center justify-between">
            <h3 class="flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white">
              <iconify-icon
                icon="heroicons:map"
                height="20"
                width="20"
              />
              Keyboard Shortcuts
            </h3>
            <label class="flex cursor-pointer items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
              <input
                v-model="vimMode"
                type="checkbox"
                class="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700"
              >
              <span>I know what <code class="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-xs dark:bg-gray-700">:wq</code> does</span>
            </label>
          </div>

          <!-- Two Column Grid -->
          <div class="grid grid-cols-2 gap-12">
            <!-- Left Column -->
            <div class="space-y-6">
              <!-- Zoom Section -->
              <ShortcutSection title="Zoom">
                <ShortcutRow label="Zoom In">
                  <Key>{{ modifierKey }}</Key>
                  <Key>+</Key>
                </ShortcutRow>
                <ShortcutRow label="Zoom Out">
                  <Key>{{ modifierKey }}</Key>
                  <Key>-</Key>
                </ShortcutRow>
                <ShortcutRow label="Zoom to Fit">
                  <Key>{{ modifierKey }}</Key>
                  <Key>0</Key>
                </ShortcutRow>
              </ShortcutSection>

              <!-- Edit Section -->
              <ShortcutSection title="Edit">
                <ShortcutRow label="Undo">
                  <template v-if="vimMode">
                    <Key>U</Key>
                  </template>
                  <template v-else>
                    <Key>{{ modifierKey }}</Key>
                    <Key>Z</Key>
                  </template>
                </ShortcutRow>
                <ShortcutRow label="Redo">
                  <template v-if="vimMode">
                    <Key>{{ modifierKey }}</Key>
                    <Key>R</Key>
                  </template>
                  <template v-else>
                    <Key>{{ modifierKey }}</Key>
                    <Key>{{ isMac ? '⇧ Z' : 'Y' }}</Key>
                  </template>
                </ShortcutRow>
              </ShortcutSection>

              <!-- Other Section -->
              <ShortcutSection title="Other">
                <ShortcutRow label="Toggle Node Panel">
                  <Key>{{ modifierKey }}</Key>
                  <Key>,</Key>
                </ShortcutRow>
                <ShortcutRow label="Toggle Minimap">
                  <Key>{{ modifierKey }}</Key>
                  <Key>M</Key>
                </ShortcutRow>
                <ShortcutRow label="Search Flow">
                  <Key>S</Key>
                </ShortcutRow>
                <ShortcutRow label="Show Shortcuts">
                  <Key>?</Key>
                </ShortcutRow>
              </ShortcutSection>
            </div>

            <!-- Right Column -->
            <div class="space-y-6">
              <!-- Navigation Section -->
              <ShortcutSection title="Navigation">
                <ShortcutRow label="Pan">
                  <template v-if="vimMode">
                    <Key>H J K L</Key>
                  </template>
                  <template v-else>
                    <Key>Arrow Keys</Key>
                  </template>
                </ShortcutRow>
                <ShortcutRow label="Half-Page Down">
                  <Key>{{ modifierKey }}</Key>
                  <Key>D</Key>
                </ShortcutRow>
                <ShortcutRow label="Half-Page Up">
                  <Key>{{ modifierKey }}</Key>
                  <Key>U</Key>
                </ShortcutRow>
                <ShortcutRow label="Jump to Top">
                  <Key>G G</Key>
                </ShortcutRow>
                <ShortcutRow label="Jump to Bottom">
                  <Key>Shift</Key>
                  <Key>G</Key>
                </ShortcutRow>
              </ShortcutSection>

              <!-- Mode Section -->
              <ShortcutSection title="Find Mode">
                <ShortcutRow label="Activate Find Mode">
                  <Key>F</Key>
                  <span class="mx-1 text-xs text-gray-400">or</span>
                  <Key>/</Key>
                </ShortcutRow>
                <ShortcutRow label="Select Element">
                  <Key>A-Z</Key>
                </ShortcutRow>
                <ShortcutRow label="Cancel">
                  <Key>Esc</Key>
                </ShortcutRow>
              </ShortcutSection>
              <p class="mt-2 text-xs text-gray-500 dark:text-gray-400">
                Press F or / to show letter badges on dropzones and nodes. Type the letter to insert a node or open an existing one for editing.
              </p>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { useStorage } from '@vueuse/core'

defineProps<{
  visible: boolean
}>()

const emit = defineEmits<{
  close: []
}>()

// Vim mode preference - persisted to localStorage
const vimMode = useStorage('flow-builder-vim-mode', false)

// Detect if user is on Mac for showing correct modifier keys
const isMac = computed(() => {
  if (typeof navigator === 'undefined') return false
  return /Mac|iPod|iPhone|iPad/.test(navigator.platform)
})

// Modifier key symbols based on OS
const modifierKey = computed(() => (isMac.value ? '⌘' : 'Ctrl'))

// Dismiss on ESC key
onKeyStroke('Escape', () => {
  emit('close')
})

// Reusable sub-components
const ShortcutSection = defineComponent({
  props: {
    title: { type: String, required: true }
  },
  setup(props, { slots }) {
    return () =>
      h('div', { class: 'space-y-3' }, [
        h(
          'p',
          {
            class:
              'mb-2 text-xs font-medium uppercase tracking-wider text-gray-400 dark:text-gray-500'
          },
          props.title
        ),
        slots.default?.()
      ])
  }
})

const ShortcutRow = defineComponent({
  props: {
    label: { type: String, required: true }
  },
  setup(props, { slots }) {
    return () =>
      h('div', { class: 'flex items-center justify-between' }, [
        h('span', { class: 'text-sm text-gray-600 dark:text-gray-300' }, props.label),
        h('div', { class: 'flex items-center gap-1' }, slots.default?.())
      ])
  }
})

const Key = defineComponent({
  setup(_, { slots }) {
    return () =>
      h(
        'kbd',
        {
          class:
            'rounded border border-gray-300 bg-gray-100 px-2 py-1 text-xs font-semibold text-gray-800 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200'
        },
        slots.default?.()
      )
  }
})
</script>
